import React, { useState, useEffect, useRef, memo } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Autocomplete,
    Select,
    MenuItem,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { inputStyles, getInputStyles } from './styles';
import { validatePersonalInfo, getMissingPersonalFields } from './validation';
import { DAYS, MONTHS, YEAR_RANGE, parseDateString, formatDateComponents } from './constants';
import countries from '../../countries.json';

const PersonalInfoSection = memo(({ 
    initialData, 
    authAxios, 
    setError, 
    onUpdate 
}) => {
    // Refs for uncontrolled inputs - better performance
    const firstNameRef = useRef(null);
    const lastNameRef = useRef(null);
    const mobileRef = useRef(null);
    const address1Ref = useRef(null);
    const address2Ref = useRef(null);
    const cityRef = useRef(null);
    const stateRef = useRef(null);
    const postalCodeRef = useRef(null);
    const linkedinRef = useRef(null);

    // State for country (dropdown needs controlled)
    const [country, setCountry] = useState(initialData?.country || '');
    const [saving, setSaving] = useState(false);
    const [showErrors, setShowErrors] = useState(true);
    
    // DOB state (day, month, year dropdowns)
    const [dobDay, setDobDay] = useState('');
    const [dobMonth, setDobMonth] = useState('');
    const [dobYear, setDobYear] = useState('');
    
    const [formValues, setFormValues] = useState({
        first_name: initialData?.first_name || '',
        last_name: initialData?.last_name || '',
        mobile_number: initialData?.mobile_number || '',
        date_of_birth: initialData?.date_of_birth || '',
        address_line_1: initialData?.address_line_1 || '',
        address_line_2: initialData?.address_line_2 || '',
        city: initialData?.city || '',
        state: initialData?.state || '',
        postal_code: initialData?.postal_code || '',
        linkedin_url: initialData?.linkedin_url || '',
    });

    // Update form values when initial data changes
    useEffect(() => {
        if (initialData) {
            setFormValues({
                first_name: initialData.first_name || '',
                last_name: initialData.last_name || '',
                mobile_number: initialData.mobile_number || '',
                date_of_birth: initialData.date_of_birth || '',
                address_line_1: initialData.address_line_1 || '',
                address_line_2: initialData.address_line_2 || '',
                city: initialData.city || '',
                state: initialData.state || '',
                postal_code: initialData.postal_code || '',
                linkedin_url: initialData.linkedin_url || '',
            });
            setCountry(initialData.country || '');
            
            // Parse DOB from YYYY-MM-DD format
            const { day, month, year } = parseDateString(initialData.date_of_birth);
            setDobDay(day);
            setDobMonth(month);
            setDobYear(year);
        }
    }, [initialData]);

    // Get current form data from refs
    const getCurrentFormData = () => ({
        first_name: firstNameRef.current?.value || formValues.first_name,
        last_name: lastNameRef.current?.value || formValues.last_name,
        mobile_number: mobileRef.current?.value || formValues.mobile_number,
        date_of_birth: formatDateComponents(dobDay, dobMonth, dobYear),
        dob_day: dobDay,
        dob_month: dobMonth,
        dob_year: dobYear,
        address_line_1: address1Ref.current?.value || formValues.address_line_1,
        address_line_2: address2Ref.current?.value || formValues.address_line_2,
        city: cityRef.current?.value || formValues.city,
        state: stateRef.current?.value || formValues.state,
        country: country,
        postal_code: postalCodeRef.current?.value || formValues.postal_code,
        linkedin_url: linkedinRef.current?.value || formValues.linkedin_url,
        profile_picture_url: initialData?.profile_picture_url || '',
    });

    const handleSave = async () => {
        const currentData = getCurrentFormData();
        
        // Validate
        const errors = validatePersonalInfo(currentData);
        if (Object.keys(errors).length > 0) {
            setShowErrors(true);
            const missingFields = getMissingPersonalFields(currentData);
            setError?.(`Please fill in required fields: ${missingFields.join(', ')}`);
            return;
        }


        setSaving(true);
        
        try {
            const response = await authAxios.patch('/self/', {
                first_name: currentData.first_name,
                last_name: currentData.last_name,
                mobile_number: currentData.mobile_number,
                linkedin_url: currentData.linkedin_url,
                date_of_birth: currentData.date_of_birth || null,
                address_line_1: currentData.address_line_1,
                address_line_2: currentData.address_line_2,
                city: currentData.city,
                state: currentData.state,
                country: currentData.country,
                postal_code: currentData.postal_code,
            });

            // Update parent with saved data
            onUpdate?.(response.data);
            
            // Update local form values
            setFormValues({
                first_name: response.data.first_name || '',
                last_name: response.data.last_name || '',
                mobile_number: response.data.mobile_number || '',
                date_of_birth: response.data.date_of_birth || '',
                address_line_1: response.data.address_line_1 || '',
                address_line_2: response.data.address_line_2 || '',
                city: response.data.city || '',
                state: response.data.state || '',
                postal_code: response.data.postal_code || '',
                linkedin_url: response.data.linkedin_url || '',
            });
            setCountry(response.data.country || '');
            
            // Update DOB dropdowns from response
            const { day, month, year } = parseDateString(response.data.date_of_birth);
            setDobDay(day);
            setDobMonth(month);
            setDobYear(year);
        } catch (error) {
            console.error('Failed to save personal info:', error);
            const errorMsg = error.response?.data?.detail || error.response?.data?.message || 'Failed to save personal information';
            setError?.(errorMsg);
        } finally {
            setSaving(false);
        }
    };

    // Check if a field is empty (for error styling)
    const isEmpty = (value) => !value?.trim();
    
    // Check if DOB is complete
    const isDobIncomplete = () => !dobDay || !dobMonth || !dobYear;

    // Dropdown select styles
    const selectStyles = {
        borderRadius: '6px',
        fontSize: '0.875rem',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d1d5db',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#9ca3af',
        },
    };
    
    const errorSelectStyles = {
        ...selectStyles,
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#fca5a5',
            borderWidth: '1.5px',
        },
        backgroundColor: '#fef2f2',
    };

    return (
        <Box sx={{ mb: 6 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827' }}>
                    Personal Information
                </Typography>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    sx={{
                        bgcolor: '#4f46e5',
                        color: 'white',
                        textTransform: 'none',
                        fontWeight: 500,
                        borderRadius: '6px',
                        px: 3,
                        '&:hover': { bgcolor: '#4338ca' },
                    }}
                >
                    {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                </Button>
            </Box>

            {/* Name Row */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
                <Box>
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                        First Name<span style={{ color: '#4f46e5', marginLeft: '2px' }}>*</span>
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="First Name"
                        defaultValue={formValues.first_name}
                        inputRef={firstNameRef}
                        sx={getInputStyles(showErrors, isEmpty(formValues.first_name))}
                    />
                </Box>
                <Box>
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                        Last Name<span style={{ color: '#4f46e5', marginLeft: '2px' }}>*</span>
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Last Name"
                        defaultValue={formValues.last_name}
                        inputRef={lastNameRef}
                        sx={getInputStyles(showErrors, isEmpty(formValues.last_name))}
                    />
                </Box>
            </Box>

            {/* Phone and Date of Birth Row */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
                <Box>
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                        Phone Number<span style={{ color: '#4f46e5', marginLeft: '2px' }}>*</span>
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="+880 1XX XXX XXXX"
                        defaultValue={formValues.mobile_number}
                        inputRef={mobileRef}
                        sx={getInputStyles(showErrors, isEmpty(formValues.mobile_number))}
                    />
                </Box>
                <Box>
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                        Date of Birth<span style={{ color: '#4f46e5', marginLeft: '2px' }}>*</span>
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr 1fr', gap: 1 }}>
                        <Select
                            size="small"
                            value={dobDay}
                            onChange={(e) => setDobDay(e.target.value)}
                            displayEmpty
                            IconComponent={ExpandMoreIcon}
                            sx={showErrors && isDobIncomplete() ? errorSelectStyles : selectStyles}
                        >
                            <MenuItem value="" disabled>Day</MenuItem>
                            {DAYS.map(d => (
                                <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                            ))}
                        </Select>
                        <Select
                            size="small"
                            value={dobMonth}
                            onChange={(e) => setDobMonth(e.target.value)}
                            displayEmpty
                            IconComponent={ExpandMoreIcon}
                            sx={showErrors && isDobIncomplete() ? errorSelectStyles : selectStyles}
                        >
                            <MenuItem value="" disabled>Month</MenuItem>
                            {MONTHS.map(m => (
                                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                            ))}
                        </Select>
                        <Select
                            size="small"
                            value={dobYear}
                            onChange={(e) => setDobYear(e.target.value)}
                            displayEmpty
                            IconComponent={ExpandMoreIcon}
                            sx={showErrors && isDobIncomplete() ? errorSelectStyles : selectStyles}
                        >
                            <MenuItem value="" disabled>Year</MenuItem>
                            {YEAR_RANGE.map(y => (
                                <MenuItem key={y.value} value={y.value}>{y.label}</MenuItem>
                            ))}
                        </Select>
                    </Box>
                </Box>
            </Box>

            {/* Address Line 1 */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                    Address Line 1<span style={{ color: '#4f46e5', marginLeft: '2px' }}>*</span>
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Street address, P.O. box"
                    defaultValue={formValues.address_line_1}
                    inputRef={address1Ref}
                    sx={getInputStyles(showErrors, isEmpty(formValues.address_line_1))}
                />
            </Box>

            {/* Address Line 2 */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                    Address Line 2
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Apartment, suite, unit, building, floor, etc."
                    defaultValue={formValues.address_line_2}
                    inputRef={address2Ref}
                    sx={inputStyles}
                />
            </Box>

            {/* City and State */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
                <Box>
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                        City<span style={{ color: '#4f46e5', marginLeft: '2px' }}>*</span>
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="City"
                        defaultValue={formValues.city}
                        inputRef={cityRef}
                        sx={getInputStyles(showErrors, isEmpty(formValues.city))}
                    />
                </Box>
                <Box>
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                        State / Province<span style={{ color: '#4f46e5', marginLeft: '2px' }}>*</span>
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="State / Province"
                        defaultValue={formValues.state}
                        inputRef={stateRef}
                        sx={getInputStyles(showErrors, isEmpty(formValues.state))}
                    />
                </Box>
            </Box>

            {/* Country and Postal Code */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
                <Box>
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                        Country<span style={{ color: '#4f46e5', marginLeft: '2px' }}>*</span>
                    </Typography>
                    <Autocomplete
                        size="small"
                        options={countries}
                        value={country || null}
                        onChange={(e, newValue) => setCountry(newValue || '')}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Select or type country"
                                sx={getInputStyles(showErrors, isEmpty(country))}
                            />
                        )}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '6px',
                                fontSize: '0.875rem',
                            },
                        }}
                        freeSolo
                        autoComplete
                        autoHighlight
                    />
                </Box>
                <Box>
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                        Postal / ZIP Code
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Postal / ZIP Code"
                        defaultValue={formValues.postal_code}
                        inputRef={postalCodeRef}
                        sx={inputStyles}
                    />
                </Box>
            </Box>

            {/* LinkedIn URL */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                    LinkedIn URL
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="https://www.linkedin.com/in/..."
                    defaultValue={formValues.linkedin_url}
                    inputRef={linkedinRef}
                    sx={inputStyles}
                />
            </Box>
        </Box>
    );
});

PersonalInfoSection.displayName = 'PersonalInfoSection';

export default PersonalInfoSection;
